// Shared guard for the "parsed a non-JSON body as JSON" failure. Two distinct
// causes hide behind the same raw SyntaxError, and callers need to tell them
// apart:
//
//   'redirect'    - the body isn't JSON at all (content type isn't
//                   application/json). On an expired session an authenticated
//                   endpoint 302-redirects to the HTML login page and fetch
//                   follows it, landing a 2xx with an HTML body; a platform
//                   (nginx/Heroku) error page is the same shape on a 5xx.
//   'unparseable' - the body claimed application/json but failed to parse
//                   (truncated, empty). A genuine anomaly, not a session issue.
//
// The status separates the benign login redirect (2xx) from a platform error
// page (5xx); see isLoginRedirectError. A redirect whose target renders JSON
// (Rails 302s some report endpoints to a JSON index) is NOT flagged - the
// content type, not res.redirected, is what decides.

export type NonJsonReason = 'redirect' | 'unparseable';

/**
 * A response whose body could not be read as the requested JSON. `reason`
 * discriminates a non-JSON body ('redirect') from a malformed JSON one
 * ('unparseable', which carries the original SyntaxError as `cause`); `status`
 * lets a consumer separate a benign login redirect from a platform error page.
 */
export class NonJsonResponseError extends Error {
  readonly status: number;
  readonly reason: NonJsonReason;

  constructor(status: number, reason: NonJsonReason, options?: ErrorOptions) {
    super(`Expected a JSON response but received a ${reason} body (status ${status})`, options);
    this.name = 'NonJsonResponseError';
    this.status = status;
    this.reason = reason;
  }
}

/**
 * Whether a response's body is not JSON - decided on the content type, so a
 * redirect to a JSON endpoint parses normally and only a genuinely non-JSON
 * body (an HTML login page or error interstitial) is flagged.
 *
 * An absent content type is deliberately not flagged - a bare 200 with a JSON
 * body can omit the header, and flagging those would turn valid responses into
 * false non-JSON errors.
 */
export const isNonJsonResponse = (res: Response): boolean => {
  const contentType = res.headers?.get('content-type');
  return contentType != null && !contentType.includes('application/json');
};

/**
 * Whether the error is specifically a login-page redirect: a non-JSON body on
 * an ok/redirect status, which a consumer handles as re-auth. A non-JSON body
 * on an error status (a 5xx HTML interstitial) and a malformed JSON body are
 * both excluded - those are faults to report.
 */
export const isLoginRedirectError = (error: unknown): error is NonJsonResponseError =>
  error instanceof NonJsonResponseError && error.reason === 'redirect' && error.status >= 200 && error.status < 400;

/**
 * Parses a response body as JSON, converting a non-JSON body into a typed
 * {@link NonJsonResponseError} (reason 'redirect') and a malformed JSON body
 * into one with reason 'unparseable' and the SyntaxError as its cause - so a
 * raw SyntaxError never escapes to the reporter. Does not inspect the status; a
 * caller that treats a non-ok response specially should do so before calling.
 */
export const parseJsonResponse = async (res: Response): Promise<unknown> => {
  if (isNonJsonResponse(res)) throw new NonJsonResponseError(res.status, 'redirect');
  try {
    return await res.json();
  } catch (err) {
    if (err instanceof SyntaxError) throw new NonJsonResponseError(res.status, 'unparseable', { cause: err });
    throw err;
  }
};
